/**
 * The Admin's service-area editor (D616, ADR-0121) — framework-neutral view-models.
 *
 *  - **Operações › Área de atendimento** (`service_areas`): the area a market declares it serves is the union of its ACTIVE
 *    `operation` fences. The operator draws it on the map (one ring, or several disjoint rings — rings never have holes), saves
 *    it as the market's DRAFT, sends it for approval, and ANOTHER manager approves and publishes it (the server refuses the
 *    maker's own approval; the console never offers it). Approval publishes the next whole fence set.
 *  - This module holds what the screen needs that is not rendering: the authority, the draft model (the rings being drawn, the
 *    ring the next tap lands on, an undo stack) with pure edit helpers, the findings and the stats of the drawing (the
 *    `@vello/geofence` kernel — the same checks the server applies, so the console says "fix this" before the server would),
 *    which single action is the primary one, the decide block, the published history, and the size/GeoJSON-file helpers.
 *
 * The console only presents: the server validates every call again at the market asked for. Neutral: no country, city, unit
 * system or language is named here — the display unit comes from the caller's measurement system.
 */
import type { GeofenceSetSummaryView, GeofenceView, ServiceAreaFindingView, ServiceAreaGeometryView, ServiceAreaImpactView, ServiceAreaProposalView, ServiceAreaProposalStatusView, ServiceAreaScheduleView, ServiceAreaWeekdayView } from '@vello/api-client';
import { SERVICE_AREA_CIRCLE_SEGMENTS, SERVICE_AREA_LIMITS, SERVICE_AREA_SCHEDULE_LIMITS, circleToRing, normalizeServiceArea, overnightWindows, radiusThroughPoint, serviceAreaAsCircle, serviceAreaFindings, serviceAreaScheduleFindings, serviceAreaStats, serviceAreaToGeoJson, type GeofenceGeometry, type ServiceAreaGeometry } from '@vello/geofence';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { roundPoint } from './risk-alerts.js';

export type AreaPoint = { readonly latitude: number; readonly longitude: number };
export type AreaRing = readonly AreaPoint[];

export function serviceAreaAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'service_area:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'service_area:manage', target: 'GEO' }),
  };
}

/** The first eight characters of an operator id (the trail reads "who", not the whole uuid). */
export const shortOperatorId = (id: string): string => id.slice(0, 8);

// ---- the draft ------------------------------------------------------------------------------------------------------

/** How many edits "undo" can walk back. */
export const UNDO_DEPTH = 100;

/** How the area is being defined (D616b): a centre and a radius ("Raio"), or an outline drawn point by point ("Desenho"). */
export type AreaMode = 'radius' | 'draw';

/**
 * The radius being defined. `radiusKm` is the TEXT the operator typed (decimal point or comma - {@link parseRadiusKm} reads both), so a
 * half-typed value is kept as typed; the circle only exists while it parses.
 */
export interface RadiusState {
  readonly center?: AreaPoint | undefined;
  readonly radiusKm: string;
}

export const EMPTY_RADIUS: RadiusState = { radiusKm: '' };

/** A state of the drawing that "undo" can return to. */
interface AreaSnapshot {
  readonly rings: readonly AreaRing[];
  readonly active: number;
  readonly mode: AreaMode;
  readonly radius: RadiusState;
}

/**
 * The drawing being edited. In `radius` mode `rings` is ALWAYS the circle outline of `radius` (or empty while the centre/radius
 * is missing), so the findings, the stats, the export and the saved payload are the ordinary polygon in both modes.
 */
export interface AreaDraft {
  /** The rings being drawn, each a list of vertices (no closing duplicate). A ring with no vertex is allowed while drawing. */
  readonly rings: readonly AreaRing[];
  /** The ring the next tap on the map lands on. */
  readonly active: number;
  /** Earlier states, newest last. */
  readonly past: readonly AreaSnapshot[];
  readonly mode: AreaMode;
  readonly radius: RadiusState;
  /** True while the last edit was typing the radius: the next keystroke amends that edit instead of stacking a new one. */
  readonly typing?: true;
}

/** An empty drawing in `draw` mode (the vertex helpers' starting point). */
export const EMPTY_AREA_DRAFT: AreaDraft = { rings: [], active: 0, past: [], mode: 'draw', radius: EMPTY_RADIUS };

/** An empty area in `radius` mode - what the editor starts with: tap the centre, set the radius. */
export const NEW_AREA_DRAFT: AreaDraft = { ...EMPTY_AREA_DRAFT, mode: 'radius' };

const clampActive = (rings: readonly AreaRing[], active: number): number => Math.max(0, Math.min(Math.max(0, rings.length - 1), active));

const snapshotOf = (draft: AreaDraft): AreaSnapshot => ({ rings: draft.rings, active: draft.active, mode: draft.mode, radius: draft.radius });

/** The next state, remembering the current one for undo. `next` changes the mode / radius too (one undoable edit). */
function step(draft: AreaDraft, rings: readonly AreaRing[], active: number, next?: { readonly mode?: AreaMode; readonly radius?: RadiusState }): AreaDraft {
  return { rings, active: clampActive(rings, active), past: [...draft.past, snapshotOf(draft)].slice(-UNDO_DEPTH), mode: next?.mode ?? draft.mode, radius: next?.radius ?? draft.radius };
}

/** A tap on the map: the point (rounded) joins the active ring; the first tap creates the first ring. */
export function addVertex(draft: AreaDraft, point: AreaPoint): AreaDraft {
  const p = roundPoint(point);
  if (draft.rings.length === 0) return step(draft, [[p]], 0);
  const at = clampActive(draft.rings, draft.active);
  const total = draft.rings.reduce((n, r) => n + r.length, 0);
  if (draft.rings[at]!.length >= SERVICE_AREA_LIMITS.maxVerticesPerRing || total >= SERVICE_AREA_LIMITS.maxVertices) return draft;
  return step(draft, draft.rings.map((r, i) => (i === at ? [...r, p] : r)), at);
}

/** Replace one vertex (a typed correction). A position that is not on Earth is ignored. */
export function moveVertex(draft: AreaDraft, ring: number, index: number, point: AreaPoint): AreaDraft {
  const p = roundPoint(point);
  if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude) || Math.abs(p.latitude) > 90 || Math.abs(p.longitude) > 180) return draft;
  const target = draft.rings[ring];
  if (target === undefined || index < 0 || index >= target.length) return draft;
  return step(draft, draft.rings.map((r, i) => (i === ring ? r.map((v, j) => (j === index ? p : v)) : r)), draft.active);
}

/** Add a vertex INSIDE a ring, at position `atIndex` (0..length; `length` appends): the "split this edge" edit that keeps a drawing simple. */
export function insertVertex(draft: AreaDraft, ring: number, atIndex: number, point: AreaPoint): AreaDraft {
  const target = draft.rings[ring];
  if (target === undefined || !Number.isInteger(atIndex) || atIndex < 0 || atIndex > target.length) return draft;
  const p = roundPoint(point);
  if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude) || Math.abs(p.latitude) > 90 || Math.abs(p.longitude) > 180) return draft;
  const total = draft.rings.reduce((n, r) => n + r.length, 0);
  if (target.length >= SERVICE_AREA_LIMITS.maxVerticesPerRing || total >= SERVICE_AREA_LIMITS.maxVertices) return draft;
  return step(draft, draft.rings.map((r, i) => (i === ring ? [...r.slice(0, atIndex), p, ...r.slice(atIndex)] : r)), draft.active);
}

/** The middle of edge `edge` of a ring (from vertex `edge` to the next one, the last edge closing the ring); `undefined` when there is no such edge. */
export function edgeMidpoint(ring: AreaRing, edge: number): AreaPoint | undefined {
  const a = ring[edge];
  const b = ring[(edge + 1) % ring.length];
  if (ring.length < 2 || a === undefined || b === undefined || (ring.length === 2 && edge > 0)) return undefined;
  return roundPoint({ latitude: (a.latitude + b.latitude) / 2, longitude: (a.longitude + b.longitude) / 2 });
}

export function deleteVertex(draft: AreaDraft, ring: number, index: number): AreaDraft {
  const target = draft.rings[ring];
  if (target === undefined || index < 0 || index >= target.length) return draft;
  return step(draft, draft.rings.map((r, i) => (i === ring ? r.filter((_, j) => j !== index) : r)), draft.active);
}

/** A new, empty ring becomes the active one (refused at the ring limit, and while the active ring is still empty). */
export function addRing(draft: AreaDraft): AreaDraft {
  if (draft.rings.length >= SERVICE_AREA_LIMITS.maxRings) return draft;
  if (draft.rings.length > 0 && draft.rings[clampActive(draft.rings, draft.active)]!.length === 0) return draft;
  return step(draft, [...draft.rings, []], draft.rings.length);
}

export function deleteRing(draft: AreaDraft, ring: number): AreaDraft {
  if (ring < 0 || ring >= draft.rings.length) return draft;
  const rings = draft.rings.filter((_, i) => i !== ring);
  return step(draft, rings, draft.active > ring || draft.active >= rings.length ? draft.active - 1 : draft.active);
}

/** Choose the ring the next tap lands on (not an edit: it does not enter the undo stack). */
export function selectRing(draft: AreaDraft, ring: number): AreaDraft {
  return ring < 0 || ring >= draft.rings.length ? draft : { ...draft, active: ring };
}

export function undoDraft(draft: AreaDraft): AreaDraft {
  const previous = draft.past[draft.past.length - 1];
  return previous === undefined ? draft : { rings: previous.rings, active: clampActive(previous.rings, previous.active), past: draft.past.slice(0, -1), mode: previous.mode, radius: previous.radius };
}

/** Clear the whole drawing (an edit, so it can be undone). */
export function clearDraft(draft: AreaDraft): AreaDraft {
  if (draft.mode === 'radius') return draft.rings.length === 0 && draft.radius.center === undefined ? draft : step(draft, [], 0, { radius: { radiusKm: draft.radius.radiusKm } });
  return draft.rings.length === 0 ? draft : step(draft, [], 0);
}

/**
 * Replace the drawing with these rings as ONE edit (the published area, an import): "undo" brings the old drawing back. A lone
 * ring that is a radius ({@link serviceAreaAsCircle}) opens in `radius` mode with its centre and radius, anything else in `draw` mode.
 */
export function replaceRings(draft: AreaDraft, rings: readonly AreaRing[], locale = DEFAULT_LOCALE): AreaDraft {
  const rounded = rings.map((r) => r.map(roundPoint));
  const radius = recognisedRadius(rounded, locale);
  return step(draft, rounded, rounded.length - 1, radius !== undefined ? { mode: 'radius', radius } : rounded.length > 0 ? { mode: 'draw' } : undefined);
}

/** Replace the drawing with these rings (a saved draft); a fresh undo stack, in `radius` mode when the rings are a radius. */
export function draftFromRings(rings: readonly AreaRing[], locale = DEFAULT_LOCALE): AreaDraft {
  const rounded = rings.map((r) => r.map(roundPoint));
  const radius = recognisedRadius(rounded, locale);
  return { rings: rounded, active: Math.max(0, rounded.length - 1), past: [], mode: radius !== undefined ? 'radius' : 'draw', radius: radius ?? EMPTY_RADIUS };
}

/** Rings of a geometry as the editor draws them: a polygon or multipolygon as it is, a circle as a polygon, a corridor not at all. */
export function ringsOfGeometry(g: GeofenceView['geometry']): readonly AreaRing[] {
  if (g.kind === 'polygon') return [dropClosing(g.ring)];
  if (g.kind === 'multipolygon') return g.polygons.map(dropClosing);
  if (g.kind === 'circle') return [circleToRing(g.center, g.radiusMeters)];
  return [];
}

function dropClosing(ring: AreaRing): AreaRing {
  const first = ring[0];
  const last = ring[ring.length - 1];
  return ring.length > 1 && first !== undefined && last !== undefined && first.latitude === last.latitude && first.longitude === last.longitude ? ring.slice(0, -1) : ring;
}

/** The published area as a starting draft: every `operation` fence of a set (circles become polygons). */
export function draftFromFences(fences: readonly GeofenceView[], locale = DEFAULT_LOCALE): AreaDraft {
  return draftFromRings(fences.filter((f) => f.purpose === 'operation').flatMap((f) => ringsOfGeometry(f.geometry)), locale);
}

/** The draft saved on the server, as a drawing. */
export function draftFromProposal(proposal: ServiceAreaProposalView, locale = DEFAULT_LOCALE): AreaDraft {
  return draftFromRings(ringsOfGeometry(proposal.geometry), locale);
}

/** The geometry the draft stands for (rings with no vertex are left out): none, a polygon (one ring) or a multipolygon. */
export function draftGeometry(draft: AreaDraft): ServiceAreaGeometry | undefined {
  const rings = draft.rings.filter((r) => r.length > 0);
  if (rings.length === 0) return undefined;
  return rings.length === 1 ? { kind: 'polygon', ring: rings[0]! } : { kind: 'multipolygon', polygons: rings };
}

/** The wire form of the draft geometry (structurally the same shape). */
export function draftGeometryView(draft: AreaDraft): ServiceAreaGeometryView | undefined {
  return draftGeometry(draft) as ServiceAreaGeometryView | undefined;
}

/** Ring numbers of the draft (with empty rings counted) in the order of the non-empty ones the kernel sees. */
function filledRingNumbers(draft: AreaDraft): readonly number[] {
  return draft.rings.flatMap((r, i) => (r.length > 0 ? [i] : []));
}

export interface AreaFinding {
  readonly issue: string;
  /** 0-based ring of the DRAFT the finding concerns; absent for whole-drawing findings. */
  readonly ring?: number;
}

/**
 * Every finding of the drawing (empty = it can be sent for approval). The market centre, when known, must fall inside. The server
 * judges the NORMALIZED geometry (7 decimals, closing duplicate dropped) that it stores, so the findings are computed on it too:
 * two vertices closer than the rounding step are one vertex to the server (REPEATED_VERTEX at submit) and must be one here.
 */
export function draftFindings(draft: AreaDraft, center?: AreaPoint): readonly AreaFinding[] {
  const geometry = draftGeometry(draft);
  if (geometry === undefined) return [{ issue: 'SERVICE_AREA_EMPTY' }];
  const numbers = filledRingNumbers(draft);
  return serviceAreaFindings(normalizeServiceArea(geometry), { center }).map((f) => ({ issue: f.issue, ...(f.ring !== undefined ? { ring: numbers[f.ring] ?? f.ring } : {}) }));
}

/** The findings the server reported for the saved proposal (its rings are the saved drawing's own). */
export const findingsFromView = (findings: readonly ServiceAreaFindingView[]): readonly AreaFinding[] => findings.map((f) => ({ issue: f.issue, ...(f.ring !== undefined ? { ring: f.ring } : {}) }));

export interface DraftStats {
  readonly areaSquareMeters: number;
  readonly perimeterMeters: number;
  readonly vertices: number;
  readonly rings: number;
}

/** Area, perimeter and counts of the drawing, or `undefined` while it has no vertex or a ring is too short to measure. */
export function draftStats(draft: AreaDraft): DraftStats | undefined {
  const geometry = draftGeometry(draft);
  if (geometry === undefined) return undefined;
  if ((geometry.kind === 'polygon' ? [geometry.ring] : geometry.polygons).some((r) => r.length < 3)) return { areaSquareMeters: 0, perimeterMeters: 0, vertices: draft.rings.reduce((n, r) => n + r.length, 0), rings: draft.rings.filter((r) => r.length > 0).length };
  return serviceAreaStats(geometry);
}

/** The size of the area in the unit the market's measurement system reads: hectares (metric) or acres (imperial), localized. */
export function formatAreaSize(areaSquareMeters: number, measurement: 'metric' | 'imperial', locale: string): string {
  const imperial = measurement === 'imperial';
  const value = imperial ? areaSquareMeters / 4046.8564224 : areaSquareMeters / 10_000;
  return new Intl.NumberFormat(locale, { style: 'unit', unit: imperial ? 'acre' : 'hectare', unitDisplay: 'short', maximumFractionDigits: value < 100 ? 1 : 0 }).format(value);
}

// ---- radius mode (D616b) ---------------------------------------------------------------------------------------------

const DEFAULT_LOCALE = 'en';

/** The fraction of r2 a regular {@link SERVICE_AREA_CIRCLE_SEGMENTS}-gon covers (slightly under pi). */
const OUTLINE_AREA_FACTOR = (SERVICE_AREA_CIRCLE_SEGMENTS / 2) * Math.sin((2 * Math.PI) / SERVICE_AREA_CIRCLE_SEGMENTS);

/**
 * The radii (metres) the editor accepts, derived from the area limits of the kernel: the radius whose 64-point outline has the
 * minimum / maximum area, pulled inwards by 1 % so a radius inside the limits is never flagged too small / too large by the kernel.
 */
export const RADIUS_LIMITS_METERS = {
  min: Math.ceil(Math.sqrt(SERVICE_AREA_LIMITS.minAreaSquareMeters / OUTLINE_AREA_FACTOR) * 1.01),
  max: Math.floor(Math.sqrt(SERVICE_AREA_LIMITS.maxAreaSquareMeters / OUTLINE_AREA_FACTOR) * 0.99),
} as const;

/** The radius a first tap on the map starts with (metres). */
export const DEFAULT_RADIUS_METERS = 5000;

/** A radius in kilometres as the text of the field: up to three decimals (metres), no grouping, the locale's decimal sign. */
export function formatRadiusKm(radiusMeters: number, locale = DEFAULT_LOCALE): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 3, useGrouping: false }).format(radiusMeters / 1000);
}

/** Read the kilometres an operator typed ("12.5" or "12,5"; nothing else - no sign, no unit, no thousands separator), or `undefined`. */
export function parseRadiusKm(text: string): number | undefined {
  const m = /^\s*(\d+)(?:[.,](\d{1,9}))?\s*$/.exec(text);
  if (m === null) return undefined;
  const km = Number(`${m[1]}.${m[2] ?? '0'}`);
  return Number.isFinite(km) ? km : undefined;
}

/** The radius in whole metres, or `undefined` while the text is empty, unreadable or zero. */
export function radiusMetersOf(radius: RadiusState): number | undefined {
  const km = parseRadiusKm(radius.radiusKm);
  if (km === undefined) return undefined;
  const meters = Math.round(km * 1000);
  return meters > 0 ? meters : undefined;
}

/** What is missing or wrong with the radius, for the message under the field (empty = nothing to say). */
export type RadiusIssue = 'CENTER_REQUIRED' | 'RADIUS_REQUIRED' | 'RADIUS_UNREADABLE' | 'RADIUS_TOO_SMALL' | 'RADIUS_TOO_LARGE';

export function radiusIssue(radius: RadiusState): RadiusIssue | undefined {
  if (radius.radiusKm.trim() === '') return 'RADIUS_REQUIRED';
  const meters = radiusMetersOf(radius);
  if (meters === undefined) return 'RADIUS_UNREADABLE';
  if (meters < RADIUS_LIMITS_METERS.min) return 'RADIUS_TOO_SMALL';
  if (meters > RADIUS_LIMITS_METERS.max) return 'RADIUS_TOO_LARGE';
  return radius.center === undefined ? 'CENTER_REQUIRED' : undefined;
}

/** The issues that stop an area in `radius` mode from being sent (none in `draw` mode, where the kernel's findings speak). */
export function draftRadiusIssues(draft: AreaDraft): readonly RadiusIssue[] {
  if (draft.mode !== 'radius') return [];
  const issue = radiusIssue(draft.radius);
  return issue === undefined ? [] : [issue];
}

/** The outline of a radius (64 points), or no ring while the centre or the radius is missing. */
export function radiusRings(radius: RadiusState): readonly AreaRing[] {
  const meters = radiusMetersOf(radius);
  if (radius.center === undefined || meters === undefined) return [];
  const ring = circleToRing(radius.center, meters, SERVICE_AREA_CIRCLE_SEGMENTS);
  return ring.length === 0 ? [] : [ring];
}

/** Whether these rings are a radius (a lone circle outline), with the state it opens in. */
function recognisedRadius(rings: readonly AreaRing[], locale: string): RadiusState | undefined {
  if (rings.length !== 1) return undefined;
  const circle = serviceAreaAsCircle({ kind: 'polygon', ring: rings[0]! });
  return circle === undefined ? undefined : { center: circle.center, radiusKm: formatRadiusKm(circle.radiusMeters, locale) };
}

/** The radius (whole metres) of a geometry, when it is one: a circle fence, or the 64-point outline of a radius. */
export function geometryRadiusMeters(geometry: GeofenceView['geometry'] | ServiceAreaProposalView['geometry']): number | undefined {
  return serviceAreaAsCircle(geometry as GeofenceGeometry)?.radiusMeters;
}

/** The radius of a published area: the lone `operation` fence of a set, when that fence is a radius. */
export function fencesRadiusMeters(fences: readonly Pick<GeofenceView, 'purpose' | 'geometry'>[]): number | undefined {
  const operation = fences.filter((f) => f.purpose === 'operation');
  return operation.length === 1 ? geometryRadiusMeters(operation[0]!.geometry) : undefined;
}

function withRadius(draft: AreaDraft, radius: RadiusState): AreaDraft {
  const rings = radiusRings(radius);
  return step(draft, rings, 0, { mode: 'radius', radius });
}

/** A tap on the map in `radius` mode: the centre moves there (rounded); an empty radius starts at {@link DEFAULT_RADIUS_METERS}. */
export function setRadiusCenter(draft: AreaDraft, point: AreaPoint, locale = DEFAULT_LOCALE): AreaDraft {
  const p = roundPoint(point);
  if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude) || Math.abs(p.latitude) > 90 || Math.abs(p.longitude) > 180) return draft;
  const radiusKm = draft.radius.radiusKm.trim() === '' ? formatRadiusKm(DEFAULT_RADIUS_METERS, locale) : draft.radius.radiusKm;
  return withRadius(draft, { center: p, radiusKm });
}

/** Typing in the radius field. A run of keystrokes is ONE undoable edit. */
export function setRadiusText(draft: AreaDraft, text: string): AreaDraft {
  const radius: RadiusState = { center: draft.radius.center, radiusKm: text };
  const rings = radiusRings(radius);
  if (draft.typing === true && draft.mode === 'radius') return { ...draft, rings, active: 0, radius };
  return { ...withRadius(draft, radius), typing: true };
}

/** A dragged handle released at this radius (rounded to 10 m): one undoable edit. */
export function setRadiusMeters(draft: AreaDraft, radiusMeters: number, locale = DEFAULT_LOCALE): AreaDraft {
  if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) return draft;
  const rounded = Math.max(10, Math.round(radiusMeters / 10) * 10);
  return withRadius(draft, { center: draft.radius.center, radiusKm: formatRadiusKm(rounded, locale) });
}

/** The mean of the vertices of every ring (rounded), or `undefined` for a drawing with no vertex. */
function centroidOf(rings: readonly AreaRing[]): AreaPoint | undefined {
  const all = rings.flat();
  if (all.length === 0) return undefined;
  return roundPoint({ latitude: all.reduce((n, p) => n + p.latitude, 0) / all.length, longitude: all.reduce((n, p) => n + p.longitude, 0) / all.length });
}

/**
 * Switch between "Raio" and "Desenho" as ONE undoable edit.
 *  - radius -> draw: the circle outline stays as the (now editable) 64-point drawing.
 *  - draw -> radius: a drawing that is a circle gets its own centre and radius back; any other drawing becomes a circle around its
 *    centroid (the mean of its points) whose radius is the mean distance of the points from it (to 10 m); an empty drawing starts at
 *    `marketCenter` (when the market has one) with the radius already typed, or the default.
 */
export function switchAreaMode(draft: AreaDraft, mode: AreaMode, opts: { readonly marketCenter?: AreaPoint | undefined; readonly locale?: string } = {}): AreaDraft {
  if (draft.mode === mode) return draft;
  if (mode === 'draw') return step(draft, draft.rings, 0, { mode: 'draw' });
  const locale = opts.locale ?? DEFAULT_LOCALE;
  const filled = draft.rings.filter((r) => r.length > 0);
  const exact = recognisedRadius(filled, locale);
  if (exact !== undefined) return withRadius(draft, exact);
  const centroid = centroidOf(filled);
  const kept = radiusMetersOf(draft.radius) !== undefined ? draft.radius.radiusKm : formatRadiusKm(DEFAULT_RADIUS_METERS, locale);
  if (centroid === undefined) {
    const market = opts.marketCenter === undefined ? undefined : roundPoint(opts.marketCenter);
    return withRadius(draft, { center: market, radiusKm: market === undefined ? draft.radius.radiusKm : kept });
  }
  const points = filled.flat();
  const mean = points.length >= 3 ? points.reduce((n, p) => n + radiusThroughPoint(centroid, p), 0) / points.length : DEFAULT_RADIUS_METERS;
  const meters = Number.isFinite(mean) && mean > 0 ? Math.max(10, Math.round(mean / 10) * 10) : DEFAULT_RADIUS_METERS;
  return withRadius(draft, { center: centroid, radiusKm: formatRadiusKm(meters, locale) });
}

// ---- the actions ----------------------------------------------------------------------------------------------------

export type ServiceAreaPrimaryAction = 'SAVE' | 'SUBMIT' | 'APPROVE';

export type ServiceAreaOpen = Pick<ServiceAreaProposalView, 'status' | 'proposedBy'>;

/**
 * The ONE primary action the screen shows (design rule: a single primary per screen):
 *  - another manager sees a SUBMITTED proposal → approve and publish it;
 *  - the author with unsaved changes (and a drawing) → save the draft; once saved and without findings → send for approval;
 *  - everything else is secondary (reject, withdraw, clear) or waits for someone else.
 */
export function serviceAreaPrimaryAction(input: {
  readonly mayManage: boolean;
  readonly operatorId: string | undefined;
  readonly open: ServiceAreaOpen | undefined;
  readonly dirty: boolean;
  readonly hasDrawing: boolean;
  readonly findingCount: number;
  /** D868: a GLOBAL super administrator (`maySelfApprove`) approves their own submitted proposal. */
  readonly selfApproval?: boolean;
}): ServiceAreaPrimaryAction | undefined {
  if (!input.mayManage) return undefined;
  const { open } = input;
  if (open !== undefined && open.status === 'SUBMITTED') return input.operatorId !== undefined && (open.proposedBy !== input.operatorId || input.selfApproval === true) ? 'APPROVE' : undefined;
  // A draft of another operator is theirs to edit: the server allows one open proposal per market.
  if (open !== undefined && input.operatorId !== undefined && open.proposedBy !== input.operatorId) return undefined;
  if (input.dirty) return input.hasDrawing ? 'SAVE' : undefined;
  if (open !== undefined && open.status === 'DRAFT' && input.findingCount === 0) return 'SUBMIT';
  return undefined;
}

/** Why the signed-in operator cannot decide the open proposal (undefined → may approve or reject it). */
export function areaDecideBlock(proposal: ServiceAreaOpen | undefined, operatorId: string | undefined, mayManage: boolean, selfApproval = false): 'NOT_MANAGER' | 'NOT_SUBMITTED' | 'OWN_PROPOSAL' | undefined {
  if (!mayManage) return 'NOT_MANAGER';
  if (proposal === undefined || proposal.status !== 'SUBMITTED') return 'NOT_SUBMITTED';
  if (!selfApproval && operatorId !== undefined && proposal.proposedBy === operatorId) return 'OWN_PROPOSAL';
  return undefined;
}

/** Whether the operator may edit the open proposal's drawing (nothing open, or his own draft; a submitted one is withdrawn first). */
export function mayEditArea(mayManage: boolean, operatorId: string | undefined, open: ServiceAreaOpen | undefined): boolean {
  if (!mayManage) return false;
  if (open === undefined) return true;
  return open.status === 'DRAFT' && operatorId !== undefined && open.proposedBy === operatorId;
}

export type AreaTone = 'neutral' | 'success' | 'warning' | 'danger';
export const proposalStatusTone = (status: ServiceAreaProposalStatusView): AreaTone => (status === 'PUBLISHED' ? 'success' : status === 'SUBMITTED' ? 'warning' : status === 'REJECTED' ? 'danger' : 'neutral');

// ---- the operating hours --------------------------------------------------------------------------------------------

export type AreaWeekday = ServiceAreaWeekdayView;
export const AREA_WEEKDAYS: readonly AreaWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** One line of the hours editor: the days it applies to, a start and an end typed as the market's local `HH:MM`. */
export interface HoursRow {
  readonly days: readonly AreaWeekday[];
  readonly start: string;
  readonly end: string;
}

/**
 * The operating hours being edited. `defined: false` is "open 24 hours" (the saved schedule is `null`). An end EARLIER than the
 * start is an overnight window (22:00 to 02:00); an end of `00:00` is the end of the day (the time input has no `24:00`).
 */
export interface AreaHours {
  readonly defined: boolean;
  readonly rows: readonly HoursRow[];
}

export const OPEN_ALL_DAY: AreaHours = { defined: false, rows: [] };

/** What the editor starts with when the operator chooses "defined hours": every day, a working day's span. */
export const DEFAULT_HOURS_ROW: HoursRow = { days: AREA_WEEKDAYS, start: '06:00', end: '22:00' };

const sortDays = (days: readonly AreaWeekday[]): readonly AreaWeekday[] => AREA_WEEKDAYS.filter((d) => days.includes(d));

/** A saved schedule as editor rows (a window ending at `24:00` shows `00:00`; an all-day window shows `00:00` - `00:00`). */
export function hoursFromSchedule(schedule: ServiceAreaScheduleView | null | undefined): AreaHours {
  const windows = schedule?.windows;
  if (!Array.isArray(windows)) return OPEN_ALL_DAY;
  return { defined: true, rows: windows.map((w) => ({ days: sortDays(w.days), start: w.start, end: w.end === '24:00' ? '00:00' : w.end })) };
}

/** The hours of the `operation` fences of a set (one published area has one schedule; the first fence that carries one speaks). */
export function hoursFromFences(fences: readonly Pick<GeofenceView, 'purpose' | 'schedule'>[]): AreaHours {
  for (const f of fences) {
    if (f.purpose !== 'operation') continue;
    const s = f.schedule as ServiceAreaScheduleView | null | undefined;
    if (s !== null && s !== undefined && Array.isArray(s.windows)) return hoursFromSchedule(s);
  }
  return OPEN_ALL_DAY;
}

/** The windows the rows stand for: an overnight row becomes the pair the schedule model needs. */
export function hoursWindows(rows: readonly HoursRow[]): ServiceAreaScheduleView['windows'] {
  return rows.flatMap((r) => (r.start === '00:00' && r.end === '00:00' ? [{ days: r.days, start: '00:00', end: '24:00' }] : overnightWindows(r.days, r.start, r.end)));
}

/** The schedule to save: `null` for open 24 hours. */
export function hoursToSchedule(hours: AreaHours): ServiceAreaScheduleView | null {
  return hours.defined ? { windows: hoursWindows(hours.rows) } : null;
}

/** Findings of the hours (empty = publishable); the same checks the server runs. Open 24 hours has none. */
export function hoursFindings(hours: AreaHours): readonly AreaFinding[] {
  const schedule = hoursToSchedule(hours);
  return schedule === null ? [] : serviceAreaScheduleFindings(schedule).map((f) => ({ issue: f.issue }));
}

export const MAX_HOURS_ROWS = SERVICE_AREA_SCHEDULE_LIMITS.maxWindows;

export function addHoursRow(hours: AreaHours): AreaHours {
  return hours.rows.length >= MAX_HOURS_ROWS ? hours : { defined: true, rows: [...hours.rows, DEFAULT_HOURS_ROW] };
}
export function removeHoursRow(hours: AreaHours, index: number): AreaHours {
  return { defined: hours.defined, rows: hours.rows.filter((_, i) => i !== index) };
}
export function setHoursRow(hours: AreaHours, index: number, patch: Partial<HoursRow>): AreaHours {
  return { defined: hours.defined, rows: hours.rows.map((r, i) => (i === index ? { ...r, ...patch, days: sortDays(patch.days ?? r.days) } : r)) };
}
export function toggleHoursDay(hours: AreaHours, index: number, day: AreaWeekday, on: boolean): AreaHours {
  const row = hours.rows[index];
  if (row === undefined) return hours;
  return setHoursRow(hours, index, { days: on ? [...row.days, day] : row.days.filter((d) => d !== day) });
}
/** "Open 24 hours" (`false`) or "defined hours" (`true`, starting with one row when there is none). */
export function setHoursDefined(hours: AreaHours, defined: boolean): AreaHours {
  if (!defined) return { defined: false, rows: hours.rows };
  return { defined: true, rows: hours.rows.length > 0 ? hours.rows : [DEFAULT_HOURS_ROW] };
}

/** Whether a row ends the next day (the editor says so beside it). */
export const isOvernight = (row: HoursRow): boolean => row.end !== '00:00' && row.end < row.start;

/**
 * The hours as short text per window - "Mon-Fri 06:00-23:00" - consecutive days of three or more joined by a dash. `dayName` is the
 * caller's localized short name; the dash and the separators are punctuation, not words. An all-day window reads "00:00-24:00".
 */
export function formatHours(schedule: ServiceAreaScheduleView | null | undefined, dayName: (day: AreaWeekday) => string): readonly string[] {
  const windows = schedule?.windows;
  if (!Array.isArray(windows)) return [];
  return windows.map((w) => {
    const days = sortDays(w.days);
    const runs: AreaWeekday[][] = [];
    for (const d of days) {
      const last = runs[runs.length - 1];
      if (last !== undefined && AREA_WEEKDAYS.indexOf(d) === AREA_WEEKDAYS.indexOf(last[last.length - 1]!) + 1) last.push(d);
      else runs.push([d]);
    }
    const label = runs.map((r) => (r.length >= 3 ? `${dayName(r[0]!)}\u2013${dayName(r[r.length - 1]!)}` : r.map(dayName).join(', '))).join(', ');
    return `${label} ${w.start}\u2013${w.end}`;
  });
}

// ---- the approval impact ----------------------------------------------------------------------------------------------

/**
 * Whether approving needs the approver's note: the area shrinks by `shrinkConfirmPercent` or more. A threshold the server could not
 * read (`null`) asks for the note on ANY reduction. A first drawing (no change figure) never needs it. Mirrors the server's rule for
 * display only - the server refuses an approval without the note (`SHRINK_NOTE_REQUIRED`).
 */
export function shrinkNeedsNote(impact: Pick<ServiceAreaImpactView, 'changePercent' | 'shrinkConfirmPercent'> | null | undefined): boolean {
  if (impact === null || impact === undefined) return false;
  const { changePercent, shrinkConfirmPercent } = impact;
  if (changePercent === null || !Number.isFinite(changePercent)) return false;
  if (shrinkConfirmPercent === null || !Number.isFinite(shrinkConfirmPercent)) return changePercent < 0;
  return changePercent <= -(shrinkConfirmPercent > 0 ? shrinkConfirmPercent : 1);
}

/** The area change with its sign ("+12,5%", "-30%"), localized. */
export function formatChangePercent(changePercent: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'percent', signDisplay: 'exceptZero', maximumFractionDigits: 1 }).format(changePercent / 100);
}

// ---- the history ----------------------------------------------------------------------------------------------------

export interface AreaHistoryRow {
  readonly version: number;
  readonly publishedAt: string;
  readonly reasonCode: string;
  readonly publishedBy: string;
  readonly approvedBy: string;
  /** Rings of the `operation` fences of the set (what "preview on the map" draws). */
  readonly rings: readonly AreaRing[];
  readonly hasArea: boolean;
  /** The operating hours the set was published with (24 hours when none). */
  readonly hours: AreaHours;
  /** Whole metres when the set's area is a radius (a circle fence, or the outline of one); absent for a drawing. */
  readonly radiusMeters?: number;
}

/**
 * The `operation` fences that ARE a set's area. An immediate publication carries the previous fence clipped (ended) inside the same
 * set, so a set can hold the new fence `<market>:service-area:v<version>` plus ended old ones: when the new fence is present only it
 * speaks (compared as whole ids, never by text order); a seed / legacy set (no such fence) keeps the operation fences with no end or an
 * end after the set's `publishedAt`. Ended carried fences never reach the rings or the hours.
 */
export function setAreaFences(set: Pick<GeofenceSetSummaryView, 'version' | 'publishedAt' | 'operationFences'>): readonly GeofenceView[] {
  const operation = set.operationFences.filter((f) => f.purpose === 'operation');
  const own = operation.filter((f) => f.id === `${f.marketId}:service-area:v${set.version}`);
  if (own.length > 0) return own;
  const at = Date.parse(set.publishedAt);
  return operation.filter((f) => {
    if (f.effectiveTo === undefined) return true;
    const to = Date.parse(f.effectiveTo);
    return Number.isNaN(to) || Number.isNaN(at) || to > at;
  });
}

/** The published sets, newest first, as rows; a set with no `operation` fence (the market declared no area) has `hasArea: false`. */
export function areaHistoryRows(history: readonly GeofenceSetSummaryView[]): readonly AreaHistoryRow[] {
  return [...history]
    .sort((a, b) => b.version - a.version)
    .map((s) => {
      const fences = setAreaFences(s);
      const rings = fences.flatMap((f) => ringsOfGeometry(f.geometry));
      const radiusMeters = fencesRadiusMeters(fences);
      return { version: s.version, publishedAt: s.publishedAt, reasonCode: s.reasonCode, publishedBy: s.publishedBy, approvedBy: s.approvedBy, rings, hasArea: rings.length > 0, hours: hoursFromFences(fences), ...(radiusMeters !== undefined ? { radiusMeters } : {}) };
    });
}

/** The proposals other than the open one (decided or withdrawn), newest first — the trail of who tried what. */
export function closedProposals(proposals: readonly ServiceAreaProposalView[]): readonly ServiceAreaProposalView[] {
  return [...proposals].filter((p) => p.status !== 'DRAFT' && p.status !== 'SUBMITTED').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

// ---- export ---------------------------------------------------------------------------------------------------------

/** The drawing as the text of a GeoJSON file (one Feature), or `undefined` while there is nothing to export. */
export function geoJsonText(rings: readonly AreaRing[]): string | undefined {
  const filled = rings.filter((r) => r.length >= 3);
  if (filled.length === 0) return undefined;
  const geometry: ServiceAreaGeometry = filled.length === 1 ? { kind: 'polygon', ring: filled[0]! } : { kind: 'multipolygon', polygons: filled };
  return JSON.stringify(serviceAreaToGeoJson(geometry), null, 2);
}

/** A file name for the export: the market slug and the set version (or `draft`), nothing the operator typed. */
export function geoJsonFileName(market: string, version: number | 'draft'): string {
  const slug = market.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
  return `service-area-${slug === '' ? 'market' : slug}-${version === 'draft' ? 'draft' : `v${version}`}.geojson`;
}
